// PF_Analysis.jsx

// PF_Analysis.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  AttachMoney,
  AccountBalance,
  ShowChart,
  TrendingUp,
  MonetizationOn,
  Paid,
  BarChart,
  CompareArrows,
  PieChart,
  Receipt,
  CheckCircle,
  HelpOutline
} from '@mui/icons-material';
import api from '../functions/api';
import getUserId from '../functions/getUserId';

const PF_Analysis = () => {
  const portfolioData = useSelector(state => state.portfolio.data);
  const demoMode = useSelector(state => state.demoMode.isDemoMode);
  const [tickerAggregates, setTickerAggregates] = useState([]);
  const [sectorSentiments, setSectorSentiments] = useState([]);
  const [sectorExposureData, setSectorExposureData] = useState(null);
  const [tsLoading, setTsLoading] = useState(false);
  const [expoModalOpen, setExpoModalOpen] = useState(false);
  const [expoRows, setExpoRows] = useState([]);
  const [expoTitle, setExpoTitle] = useState('');

  const hasTopline = !!(portfolioData && portfolioData.portfolio_topline && portfolioData.portfolio_topline.length > 0);
  const toplineData = hasTopline ? portfolioData.portfolio_topline[0] : {};

  // Fetch TrendSearch data and sector exposures once
  useEffect(() => {
    const fetchData = async () => {
      try {
        setTsLoading(true);
        const userId = getUserId();
        
        // Fetch ticker aggregates, sector sentiments, and sector exposures
        const [tickerRes, sectorRes, exposureRes] = await Promise.all([
          api.get('/trendsearch_ticker_aggregates'),
          api.get('/trendsearch_sector_summary'),
          api.get('/pf_other_exposures', { params: { userid: userId } })
        ]);
        
        if (tickerRes?.data?.success) setTickerAggregates(tickerRes.data.data || []);
        if (sectorRes?.data?.success) setSectorSentiments(sectorRes.data.data || []);
        if (exposureRes?.data?.portfolio_various_exposures_filtered) {
          setSectorExposureData(exposureRes.data.portfolio_various_exposures_filtered);
        }
      } catch (e) {
        // noop
      } finally {
        setTsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Prefer Portfolio_Exposure totals when available from portfolio data
  const exposureHoldings = useMemo(() => {
    const pe = Array.isArray(portfolioData?.exposures) ? portfolioData.exposures : [];
    if (pe.length) {
      return pe.map(x => ({
        Ticker: (x.ticker || '').toUpperCase(),
        Company: x.company,
        Value_eur: parseFloat(x.total_amount_exposure || x.stock_exposure || 0) || 0
      }));
    }
    // Fallback to direct holdings if exposures not present
    const holdings = Array.isArray(portfolioData?.portfolio) ? portfolioData.portfolio : [];
    return holdings.map(h => ({
      Ticker: (h.Ticker || '').toUpperCase(),
      Company: h.Company,
      Value_eur: parseFloat(h.Value_eur) || 0
    }));
  }, [portfolioData]);

  // Helper function to apply blur conditionally
  const maybeBlur = (text) => demoMode ? (
    <span style={{ filter: 'blur(5px)', userSelect: 'none' }}>{text}</span>
  ) : text;

  // Helper function to render metric cards
  const MetricCard = ({ icon, title, value, secondary, tooltip }) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={1}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            {icon}
          </ListItemIcon>
          <Typography variant="subtitle1" fontWeight="medium">
            {title}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow>
              <HelpOutline fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
            </Tooltip>
          )}
        </Box>
        <Typography variant="h5" component="div">
          {maybeBlur(value)}
        </Typography>
        {secondary && (
          <Typography variant="body2" color="text.secondary">
            {secondary}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Helper function to render performance indicator
  const PerformanceIndicator = ({ value, positiveIsGood = true }) => {
    const isPositive = value >= 0;
    const color = isPositive === positiveIsGood ? 'success' : 'error';
    
    return (
      <Box display="flex" alignItems="center">
        <Chip 
          label={`${value}%`} 
          color={color} 
          size="small" 
          sx={{ mr: 1 }} 
        />
        {isPositive ? <TrendingUp color={color} /> : <ShowChart color={color} />}
      </Box>
    );
  };

  // Compute portfolio-level winner/loser shares using exposures and TS aggregates
  const trendExposure = useMemo(() => {
    if (!exposureHoldings.length || !tickerAggregates.length) return null;

    const aggByTicker = Object.create(null);
    tickerAggregates.forEach(row => {
      const t = (row.ticker || '').toUpperCase();
      aggByTicker[t] = row;
    });

    let totalValue = 0;
    let winnerValue = 0;
    let loserValue = 0;
    let enablerValue = 0;

    exposureHoldings.forEach(h => {
      const t = (h.Ticker || '').toUpperCase();
      const val = parseFloat(h.Value_eur) || 0;
      totalValue += val;
      const agg = aggByTicker[t];
      if (!agg) return; // unknown ticker in TS
      const w = parseInt(agg.winner_count || 0, 10);
      const l = parseInt(agg.loser_count || 0, 10);
      const e = parseInt(agg.enabler_count || 0, 10);
      if (w > l && w >= e) winnerValue += val;
      else if (l > w && l >= e) loserValue += val;
      else enablerValue += val; // tie / enabler dominant
    });

    const pct = (x) => totalValue > 0 ? (x / totalValue * 100) : 0;
    return {
      totalValue,
      winnerValue, loserValue, enablerValue,
      winnerPct: pct(winnerValue),
      loserPct: pct(loserValue),
      enablerPct: pct(enablerValue)
    };
  }, [exposureHoldings, tickerAggregates]);

  const openExposureDetails = (kind) => {
    if (!trendExposure) return;
    const aggByTicker = Object.create(null);
    tickerAggregates.forEach(row => { aggByTicker[(row.ticker || '').toUpperCase()] = row; });
    const rows = [];
    exposureHoldings.forEach(h => {
      const t = (h.Ticker || '').toUpperCase();
      const val = parseFloat(h.Value_eur) || 0;
      const agg = aggByTicker[t];
      if (!agg) return;
      const w = parseInt(agg.winner_count || 0, 10);
      const l = parseInt(agg.loser_count || 0, 10);
      const e = parseInt(agg.enabler_count || 0, 10);
      let label = 'enabler';
      if (w > l && w >= e) label = 'winner';
      else if (l > w && l >= e) label = 'loser';
      if (label !== kind) return;
      const pct = trendExposure.totalValue > 0 ? (val / trendExposure.totalValue * 100) : 0;
      rows.push({ ticker: h.Ticker, company: h.Company, sector: agg.sector, value: val, pct });
    });
    rows.sort((a, b) => b.value - a.value);
    const titleMap = { winner: 'Winners', loser: 'Losers', enabler: 'Enablers/Neutral' };
    setExpoRows(rows);
    setExpoTitle(`TrendSearch Exposure — ${titleMap[kind]}`);
    setExpoModalOpen(true);
  };

  // Sector exposure by TrendSearch sector sentiment (positive/neutral/negative)
  const sectorExposure = useMemo(() => {
    if (!sectorExposureData?.sector || !sectorSentiments.length) return null;
    
    // Get sector exposure percentages from fetched data
    const sectorExposures = sectorExposureData.sector;
    
    // Create sentiment lookup by sector name
    const sentimentBySector = Object.create(null);
    sectorSentiments.forEach(row => {
      const sectorName = (row.sector || '').trim();
      if (sectorName) {
        sentimentBySector[sectorName] = parseFloat(row.overall_sentiment || 0);
      }
    });

    let totalPortfolioValue = 0;
    let totalCoveredValue = 0;
    let pos = 0, neu = 0, neg = 0;
    
    sectorExposures.forEach(sectorData => {
      const sectorName = (sectorData.name || '').trim();
      const exposurePct = parseFloat(sectorData.exposure_perc || 0);
      const exposureValue = exposurePct; // This is already a percentage of total portfolio
      
      totalPortfolioValue += exposurePct;
      
      const sentiment = sentimentBySector[sectorName];
      if (sentiment !== undefined) {
        totalCoveredValue += exposurePct;
        if (sentiment >= 2) pos += exposurePct;
        else if (sentiment <= -2) neg += exposurePct;
        else neu += exposurePct;
      }
    });
    
    // Since exposure_perc is already percentage of total portfolio, we use it directly
    const coveredPct = totalCoveredValue;
    
    return { 
      totalPortfolioValue, 
      totalCoveredValue, 
      coveredPct,
      pos, neu, neg, 
      posPct: pos, 
      neuPct: neu, 
      negPct: neg,
      sectorBreakdown: sectorExposures.map(s => ({
        sector: s.name,
        exposurePct: parseFloat(s.exposure_perc || 0),
        sentiment: sentimentBySector[s.name?.trim()] || null
      })).filter(s => s.sentiment !== null)
    };
  }, [sectorExposureData, sectorSentiments]);

  const openSectorExposure = (bucket) => {
    if (!sectorExposure?.sectorBreakdown) return;
    
    const rows = [];
    sectorExposure.sectorBreakdown.forEach(sectorData => {
      const sentiment = sectorData.sentiment;
      const group = sentiment >= 2 ? 'pos' : (sentiment <= -2 ? 'neg' : 'neu');
      if (group !== bucket) return;
      
      rows.push({ 
        sector: sectorData.sector, 
        sentiment: sentiment.toFixed(2), 
        exposurePct: sectorData.exposurePct.toFixed(2) + '%',
        pct: sectorData.exposurePct
      });
    });
    
    rows.sort((a, b) => b.pct - a.pct);
    const titleMap = { pos: 'Positive Sectors', neu: 'Neutral Sectors', neg: 'Negative Sectors' };
    setExpoRows(rows); 
    setExpoTitle(`TrendSearch Sector Exposure — ${titleMap[bucket]}`); 
    setExpoModalOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Portfolio Overview</Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Key Metrics */}
        <Grid item xs={12} md={4}>
          <MetricCard icon={<AccountBalance color="primary" />} title="Total Value" value={toplineData.portfolio_total_value} tooltip="Total value of your portfolio including cash and investments" />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard icon={<AttachMoney color="primary" />} title="Cash (EUR)" value={toplineData.cash_eur} secondary={`${toplineData.cash_eur_part_of_portfolio}% of portfolio`} />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard icon={<MonetizationOn color="primary" />} title="Portfolio Result" value={toplineData.portfolio_result} />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* TrendSearch Exposure */}
        <Grid item xs={12} md={12}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">TrendSearch Exposure</Typography>
                {tsLoading && <Typography variant="caption" color="text.secondary">Loading…</Typography>}
              </Box>
              {trendExposure ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}><Typography variant="subtitle2">Winners</Typography><Box display="flex" alignItems="center" gap={1}><Chip label={`${trendExposure.winnerPct.toFixed(1)}%`} color="success" size="small" onClick={() => openExposureDetails('winner')} sx={{ cursor: 'pointer' }} /><Typography variant="body2" color="text.secondary">of portfolio value</Typography></Box></Grid>
                  <Grid item xs={12} md={4}><Typography variant="subtitle2">Losers</Typography><Box display="flex" alignItems="center" gap={1}><Chip label={`${trendExposure.loserPct.toFixed(1)}%`} color="error" size="small" onClick={() => openExposureDetails('loser')} sx={{ cursor: 'pointer' }} /><Typography variant="body2" color="text.secondary">of portfolio value</Typography></Box></Grid>
                  <Grid item xs={12} md={4}><Typography variant="subtitle2">Enablers/Neutral</Typography><Box display="flex" alignItems="center" gap={1}><Chip label={`${trendExposure.enablerPct.toFixed(1)}%`} color="warning" size="small" onClick={() => openExposureDetails('enabler')} sx={{ cursor: 'pointer' }} /><Typography variant="body2" color="text.secondary">of portfolio value</Typography></Box></Grid>
                </Grid>
              ) : (<Typography variant="body2" color="text.secondary">No TrendSearch exposure data available.</Typography>)}
            </CardContent>
          </Card>
        </Grid>

        {/* Sector Exposure by TrendSearch Sentiment */}
        <Grid item xs={12} md={12}>
          <Card variant="outlined"><CardContent><Box display="flex" alignItems="center" justifyContent="space-between" mb={1}><Typography variant="h6">Sector Exposure by TrendSearch Sentiment</Typography>{tsLoading && <Typography variant="caption" color="text.secondary">Loading…</Typography>}</Box>{sectorExposure ? (<><Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>TrendSearch covers {sectorExposure.coveredPct.toFixed(1)}% of your portfolio value. Percentages below are of total portfolio.</Typography><Grid container spacing={2}><Grid item xs={12} md={4}><Typography variant="subtitle2">Positive Sectors</Typography><Box display="flex" alignItems="center" gap={1}><Chip label={`${sectorExposure.posPct.toFixed(1)}%`} color="success" size="small" onClick={() => openSectorExposure('pos')} sx={{ cursor: 'pointer' }} /><Typography variant="body2" color="text.secondary">of portfolio value</Typography></Box></Grid><Grid item xs={12} md={4}><Typography variant="subtitle2">Neutral Sectors</Typography><Box display="flex" alignItems="center" gap={1}><Chip label={`${sectorExposure.neuPct.toFixed(1)}%`} color="default" size="small" onClick={() => openSectorExposure('neu')} sx={{ cursor: 'pointer' }} /><Typography variant="body2" color="text.secondary">of portfolio value</Typography></Box></Grid><Grid item xs={12} md={4}><Typography variant="subtitle2">Negative Sectors</Typography><Box display="flex" alignItems="center" gap={1}><Chip label={`${sectorExposure.negPct.toFixed(1)}%`} color="error" size="small" onClick={() => openSectorExposure('neg')} sx={{ cursor: 'pointer' }} /><Typography variant="body2" color="text.secondary">of portfolio value</Typography></Box></Grid></Grid></>) : (<Typography variant="body2" color="text.secondary">No sector exposure data available.</Typography>)}</CardContent></Card>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent><Typography variant="h6" gutterBottom>Performance Metrics</Typography><Grid container spacing={2}><Grid item xs={6}><Typography variant="subtitle2">Portfolio Performance</Typography>{hasTopline ? <PerformanceIndicator value={toplineData.portfolio_performance} /> : <Typography variant="body2" color="text.secondary">N/A</Typography>}</Grid><Grid item xs={6}><Typography variant="subtitle2">Performance Change</Typography>{hasTopline ? <PerformanceIndicator value={toplineData.performance_change} /> : <Typography variant="body2" color="text.secondary">N/A</Typography>}</Grid><Grid item xs={6}><Typography variant="subtitle2">Realized Results</Typography><Typography>{hasTopline ? maybeBlur(toplineData.total_realized_results_current_positions) : 'N/A'}</Typography></Grid><Grid item xs={6}><Typography variant="subtitle2">Unrealized P/L</Typography><Typography>{hasTopline ? maybeBlur(toplineData.total_unrealized_prof_loss_Eur) : 'N/A'}</Typography></Grid></Grid></CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent><Typography variant="h6" gutterBottom>Income Metrics</Typography><Grid container spacing={2}><Grid item xs={6}><Typography variant="subtitle2">Dividend Income</Typography><Typography>{hasTopline ? maybeBlur(toplineData.div_income) : 'N/A'}</Typography></Grid><Grid item xs={6}><Typography variant="subtitle2">Fixed Income Potential</Typography><Typography>{hasTopline ? maybeBlur(toplineData.fixed_income_potential) : 'N/A'}</Typography></Grid><Grid item xs={6}><Typography variant="subtitle2">Transaction Costs</Typography><Typography>{hasTopline ? toplineData.total_transactions_cost : 'N/A'}</Typography></Grid><Grid item xs={6}><Typography variant="subtitle2">Total Transactions</Typography><Typography>{hasTopline ? toplineData.total_transactions : 'N/A'}</Typography></Grid></Grid></CardContent></Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Performance Records</Typography>
          <Grid container spacing={3}><Grid item xs={12} md={6}><Typography variant="subtitle1" gutterBottom>Overall Performance</Typography><Box sx={{ mb: 2 }}><Typography variant="subtitle2">Positive Performance: {hasTopline ? toplineData.positive_performance_records : 'N/A'} ({hasTopline ? toplineData.positive_performance_records_perc : 'N/A'}%)</Typography><LinearProgress variant="determinate" value={hasTopline ? toplineData.positive_performance_records_perc : 0} color="success" sx={{ height: 8, borderRadius: 4 }} /></Box><Typography variant="body2" color="text.secondary">Unknown: {hasTopline ? toplineData.positive_performance_records_unknown : 'N/A'}</Typography></Grid><Grid item xs={12} md={6}><Typography variant="subtitle1" gutterBottom>Benchmark Comparison</Typography><Box sx={{ mb: 2 }}><Typography variant="subtitle2">Exceeding Benchmark: {hasTopline ? toplineData.exceeding_benchmark_lasttx_records : 'N/A'} ({hasTopline ? toplineData.exceeding_benchmark_lasttx_records_perc : 'N/A'}%)</Typography><LinearProgress variant="determinate" value={hasTopline ? toplineData.exceeding_benchmark_lasttx_records_perc : 0} color="info" sx={{ height: 8, borderRadius: 4 }} /></Box><Typography variant="body2" color="text.secondary">Unknown: {hasTopline ? toplineData.exceeding_benchmark_lasttx_records_unknown : 'N/A'}</Typography></Grid></Grid>
        </CardContent>
      </Card>

      <Card variant="outlined"><CardContent><Typography variant="h6" gutterBottom>Year-to-Date Performance</Typography><Grid container spacing={3}><Grid item xs={12} md={6}><Typography variant="subtitle1" gutterBottom>Positive Performance</Typography><Box sx={{ mb: 2 }}><Typography variant="subtitle2">{hasTopline ? toplineData.positive_performance_ytd_records : 'N/A'} ({hasTopline ? toplineData.positive_performance_ytd_records_perc : 'N/A'}%)</Typography><LinearProgress variant="determinate" value={hasTopline ? toplineData.positive_performance_ytd_records_perc : 0} color="success" sx={{ height: 8, borderRadius: 4 }} /></Box><Typography variant="body2" color="text.secondary">Unknown: {hasTopline ? toplineData.positive_performance_ytd_records_unknown : 'N/A'}</Typography></Grid><Grid item xs={12} md={6}><Typography variant="subtitle1" gutterBottom>Benchmark Comparison</Typography><Box sx={{ mb: 2 }}><Typography variant="subtitle2">{hasTopline ? toplineData.exceeding_benchmark_ytd_records : 'N/A'} ({hasTopline ? toplineData.exceeding_benchmark_ytd_records_perc : 'N/A'}%)</Typography><LinearProgress variant="determinate" value={hasTopline ? toplineData.exceeding_benchmark_ytd_records_perc : 0} color="info" sx={{ height: 8, borderRadius: 4 }} /></Box><Typography variant="body2" color="text.secondary">Unknown: {hasTopline ? toplineData.exceeding_benchmark_ytd_records_unknown : 'N/A'}</Typography></Grid></Grid></CardContent></Card>

      {/* Exposure detail modal */}
      <Dialog open={expoModalOpen} onClose={() => setExpoModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{expoTitle}</DialogTitle>
        <DialogContent>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {expoRows.length > 0 && expoRows[0].ticker ? (
                  <>
                    <TableCell>Ticker</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Sector</TableCell>
                    <TableCell align="right">Exposure (EUR)</TableCell>
                    <TableCell align="right">% of Portfolio</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>Sector</TableCell>
                    <TableCell align="right">TrendSearch Sentiment</TableCell>
                    <TableCell align="right">% of Portfolio</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {expoRows.map((r, i) => (
                <TableRow key={i}>
                  {r.ticker ? (
                    <>
                      <TableCell>{r.ticker}</TableCell>
                      <TableCell>{r.company}</TableCell>
                      <TableCell>{r.sector}</TableCell>
                      <TableCell align="right">{maybeBlur(r.value?.toFixed(2))}</TableCell>
                      <TableCell align="right">{r.pct.toFixed(2)}%</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{r.sector}</TableCell>
                      <TableCell align="right">{r.sentiment}</TableCell>
                      <TableCell align="right">{r.exposurePct}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PF_Analysis;


